/**
 * CR500 Phase 2B: Details Blocks Renderer
 * CR500 Phase 5C: Uses centralized debug guard
 * CR318: Standardized back-of-card layout with consistent spacing and debug boundaries
 * CR324: Chrome blocks (back_header, back_nav) separated from content for flip mode
 * 
 * Renders the details surface blocks from v2 spec.
 * Used by all presentation modes to ensure consistent content.
 * 
 * Layout contract (flip mode):
 * - back_header renders as fixed header (flex-shrink-0)
 * - Content blocks render in scrollable area (flex-1 overflow-y-auto)
 * - back_nav renders as fixed footer (flex-shrink-0)
 * 
 * All chrome is block-driven and togglable via surfaceOverrides.details.blockToggles.
 */

import React, { useMemo, useCallback } from "react";
import type { Deal } from "@/lib/types/deals";
import type { 
  ResolvedDealCardSpecV2, 
  DealCardBlockSpec,
  DealCardRenderContext,
} from "@/lib/deal-cards/specV2/types";
import { getEnabledBlocks } from "@/lib/deal-cards/specV2/resolveSpec";
import { getBlockComponent } from "@/lib/deal-cards/blocks/registry";
import { isV2DebugEnabled } from "@/lib/deal-cards/helpers";
import {
  useRenderStatusCollector,
  createBlockRenderStatus,
  getRenderReasonBadgeClass,
  type BlockRenderReason,
} from "@/lib/deal-cards/specV2";
import { cn } from "@/lib/utils";
import { useDealCardUX } from "@/contexts/DealCardUXContext";

// ================================================================
// TYPES
// ================================================================

export type DetailsRenderMode = "flip" | "modal" | "panel" | "expand";

export interface DetailsBlocksRendererProps {
  /** The deal being displayed */
  deal: Deal;
  /** The resolved v2 spec */
  spec: ResolvedDealCardSpecV2;
  /** Admin context */
  isAdmin?: boolean;
  /** Presentation mode (affects styling) */
  mode: DetailsRenderMode;
  /** Additional className */
  className?: string;
  /** Debug mode */
  debug?: boolean;
  /** Flip-back handler (flip mode only, passed to back_nav block) */
  onFlipBack?: () => void;
}

// ================================================================
// LAYOUT CONFIGURATION (CR318)
// ================================================================

/** 
 * CR318: Mode-specific spacing configuration
 * Ensures consistent layout across all presentation modes
 */
const LAYOUT_CONFIG = {
  flip: {
    containerSpacing: "space-y-3",
    blockPadding: "py-2",
    maxLinesDefault: 4,
  },
  modal: {
    containerSpacing: "space-y-4",
    blockPadding: "py-3",
    maxLinesDefault: 8,
  },
  panel: {
    containerSpacing: "space-y-4",
    blockPadding: "py-3",
    maxLinesDefault: 8,
  },
  expand: {
    containerSpacing: "space-y-3",
    blockPadding: "py-2",
    maxLinesDefault: 6,
  },
} as const;

// ================================================================
// COMPONENT
// ================================================================

export function DetailsBlocksRenderer({
  deal,
  spec,
  isAdmin = false,
  mode,
  className,
  debug: debugProp = false,
  onFlipBack,
}: DetailsBlocksRendererProps) {
  // Use centralized debug guard
  const debug = debugProp || isV2DebugEnabled();
  
  // CR316: Get render status collector
  const statusCollector = useRenderStatusCollector();
  
  // Get ALL blocks for details surface (not just enabled)
  const allDetailsBlocks = useMemo(
    () => spec.surfaces.details?.blocks || [],
    [spec]
  );
  
  // Get enabled blocks for details surface
  const enabledBlocks = useMemo(
    () => getEnabledBlocks(spec, "details"),
    [spec]
  );
  
  // CR324: Separate chrome blocks from content blocks for flip mode
  const { headerBlock, navBlock, contentBlocks } = useMemo(() => {
    const header = enabledBlocks.find(b => b.id === "back_header");
    const nav = enabledBlocks.find(b => b.id === "back_nav");
    const content = enabledBlocks.filter(b => b.id !== "back_header" && b.id !== "back_nav");
    return { headerBlock: header, navBlock: nav, contentBlocks: content };
  }, [enabledBlocks]);
  
  // Build render context (include onFlipBack for back_nav block)
  // CR451: Include flyerInteraction from UX context
  const { flyerInteraction } = useDealCardUX();
  const context: DealCardRenderContext = useMemo(() => ({
    isAdmin,
    surface: "details",
    labels: spec.resolved.labels,
    styleOverrides: spec.resolved.styleOverrides,
    onFlipBack,
    flyerInteraction,
  }), [isAdmin, spec.resolved.labels, spec.resolved.styleOverrides, onFlipBack, flyerInteraction]);
  
  // CR318: Get layout config for current mode
  const layoutConfig = LAYOUT_CONFIG[mode];
  
  // CR316: Report render status for disabled blocks
  useMemo(() => {
    if (!statusCollector) return;
    
    allDetailsBlocks.forEach(block => {
      if (!block.enabled) {
        // createBlockRenderStatus(blockId, blockType, surface, enabled, rendered, reason?, errorMessage?)
        statusCollector.reportStatus(createBlockRenderStatus(
          block.id,
          block.type,
          "details",
          false, // enabled
          false, // rendered
          "toggle_off"
        ));
      }
    });
  }, [allDetailsBlocks, statusCollector]);
  
  if (debug && isAdmin) {
    console.debug("[DetailsBlocksRenderer]", {
      mode,
      allBlockCount: allDetailsBlocks.length,
      enabledBlockCount: enabledBlocks.length,
      blockIds: enabledBlocks.map(b => b.id),
      hasHeader: !!headerBlock,
      hasNav: !!navBlock,
      contentBlockCount: contentBlocks.length,
    });
  }
  
  // Empty state - only if no content blocks AND no chrome
  const hasAnyContent = headerBlock || navBlock || contentBlocks.length > 0;
  if (!hasAnyContent) {
    return (
      <div className="text-sm text-muted-foreground italic text-center py-4 h-full flex items-center justify-center">
        No details available
      </div>
    );
  }
  
  // CR324: Flip mode uses flex layout with fixed header/footer
  if (mode === "flip") {
    return (
      <div 
        className={cn("flex flex-col h-full", className)}
        data-details-renderer="true"
        data-mode={mode}
      >
        {/* Fixed header (back_header block) */}
        {headerBlock && (
          <DetailBlockWrapper
            key={headerBlock.id}
            block={headerBlock}
            deal={deal}
            context={context}
            mode={mode}
            layoutConfig={layoutConfig}
            debug={debug}
            isFirst={true}
            isLast={false}
            isChrome={true}
          />
        )}
        
        {/* Scrollable content area */}
        <div className={cn("flex-1 overflow-y-auto p-3", layoutConfig.containerSpacing)}>
          {contentBlocks.map((block, index) => (
            <DetailBlockWrapper
              key={block.id}
              block={block}
              deal={deal}
              context={context}
              mode={mode}
              layoutConfig={layoutConfig}
              debug={debug}
              isFirst={index === 0}
              isLast={index === contentBlocks.length - 1}
            />
          ))}
        </div>
        
        {/* Fixed footer (back_nav block) */}
        {navBlock && (
          <DetailBlockWrapper
            key={navBlock.id}
            block={navBlock}
            deal={deal}
            context={context}
            mode={mode}
            layoutConfig={layoutConfig}
            debug={debug}
            isFirst={false}
            isLast={true}
            isChrome={true}
          />
        )}
      </div>
    );
  }
  
  // Non-flip modes: render all blocks in a single scrollable container
  return (
    <div 
      className={cn(layoutConfig.containerSpacing, className)}
      data-details-renderer="true"
      data-mode={mode}
    >
      {enabledBlocks.map((block, index) => (
        <DetailBlockWrapper
          key={block.id}
          block={block}
          deal={deal}
          context={context}
          mode={mode}
          layoutConfig={layoutConfig}
          debug={debug}
          isFirst={index === 0}
          isLast={index === enabledBlocks.length - 1}
        />
      ))}
    </div>
  );
}

// ================================================================
// BLOCK WRAPPER (CR318)
// ================================================================

interface DetailBlockWrapperProps {
  block: DealCardBlockSpec;
  deal: Deal;
  context: DealCardRenderContext;
  mode: DetailsRenderMode;
  layoutConfig: typeof LAYOUT_CONFIG[DetailsRenderMode];
  debug?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  /** CR324: Chrome blocks (back_header, back_nav) skip wrapper padding */
  isChrome?: boolean;
}

/**
 * CR318: Standardized wrapper for details blocks
 * CR324: Chrome blocks render without wrapper for proper flex layout
 * Provides:
 * - Consistent padding/spacing (for non-chrome blocks)
 * - Debug boundaries (CR316)
 * - Render status reporting (CR316)
 * - maxLines enforcement
 */
function DetailBlockWrapper({ 
  block, 
  deal, 
  context, 
  mode,
  layoutConfig,
  debug = false,
  isFirst = false,
  isLast = false,
  isChrome = false,
}: DetailBlockWrapperProps) {
  const Component = getBlockComponent(block.type);
  const statusCollector = useRenderStatusCollector();
  
  // Apply maxLines config from block if present, otherwise use mode default
  const configuredBlock = useMemo(() => {
    const maxLines = block.config?.maxLines ?? layoutConfig.maxLinesDefault;
    return {
      ...block,
      config: {
        ...block.config,
        maxLines,
      },
    };
  }, [block, layoutConfig.maxLinesDefault]);
  
  // CR316: Report render status callback
  const reportRenderResult = useCallback((rendered: boolean, reason: BlockRenderReason = "rendered") => {
    if (statusCollector) {
      // createBlockRenderStatus(blockId, blockType, surface, enabled, rendered, reason?, errorMessage?)
      statusCollector.reportStatus(createBlockRenderStatus(
        block.id,
        block.type,
        "details",
        block.enabled,
        rendered,
        reason
      ));
    }
  }, [statusCollector, block.id, block.type, block.enabled]);
  
  // Render the component
  const content = useMemo(() => {
    try {
      const result = <Component deal={deal} block={configuredBlock} context={context} />;
      
      // Check if component returned null (no data)
      if (result === null) {
        reportRenderResult(false, "no_data");
        return null;
      }
      
      reportRenderResult(true, "rendered");
      return result;
    } catch (error) {
      console.error(`[DetailBlockWrapper] Error rendering ${block.id}:`, error);
      reportRenderResult(false, "error");
      return null;
    }
  }, [Component, deal, configuredBlock, context, block.id, reportRenderResult]);
  
  // Don't render wrapper if content is null
  if (content === null) {
    // In debug mode, show placeholder for missing data
    if (debug) {
      return (
        <div 
          className={cn(
            "relative border border-dashed border-warning/50 rounded p-2 bg-warning/5",
            !isChrome && layoutConfig.blockPadding
          )}
          data-block-debug={block.id}
          data-block-surface="details"
          data-render-status="no_data"
        >
          <span className="absolute -top-2 left-2 text-[10px] bg-warning text-warning-foreground px-1.5 rounded font-mono">
            {block.id} (no data)
          </span>
        </div>
      );
    }
    return null;
  }
  
  // CR324: Chrome blocks render without wrapper for proper flex layout
  if (isChrome) {
    if (debug) {
      return (
        <div 
          className="relative border border-dashed border-primary/30"
          data-block-debug={block.id}
          data-block-surface="details"
          data-render-status="rendered"
          data-block-chrome="true"
        >
          <span className="absolute -top-2 left-2 z-10 text-[10px] bg-primary text-primary-foreground px-1.5 rounded font-mono">
            {block.id}
          </span>
          <span className={cn(
            "absolute -top-2 right-2 z-10 text-[9px] px-1 rounded border",
            getRenderReasonBadgeClass("rendered")
          )}>
            ✓
          </span>
          {content}
        </div>
      );
    }
    // Chrome blocks render content directly (no wrapper padding)
    return <>{content}</>;
  }
  
  // Debug mode wrapper with enhanced visibility (non-chrome blocks)
  if (debug) {
    return (
      <div 
        className={cn(
          "relative border border-dashed border-primary/30 rounded p-2",
          layoutConfig.blockPadding,
          !isLast && "border-b-0"
        )}
        data-block-debug={block.id}
        data-block-surface="details"
        data-render-status="rendered"
      >
        <span className="absolute -top-2 left-2 text-[10px] bg-primary text-primary-foreground px-1.5 rounded font-mono">
          {block.id}
        </span>
        <span className={cn(
          "absolute -top-2 right-2 text-[9px] px-1 rounded border",
          getRenderReasonBadgeClass("rendered")
        )}>
          ✓
        </span>
        {content}
      </div>
    );
  }
  
  // Standard render without debug boundaries (non-chrome blocks)
  return (
    <div 
      className={cn(
        layoutConfig.blockPadding,
        // Add subtle separator between blocks (except first)
        !isFirst && "border-t border-border/50"
      )}
      data-block-id={block.id}
    >
      {content}
    </div>
  );
}

export default DetailsBlocksRenderer;
